//************************************************************************ /
// CARD ACTIONS
// A configurable menu that displays at the bottom of a card.
//************************************************************************ /

import React, { useState } from "react";
import {
  Group,
  Tooltip,
  message,
} from "@dreambigger/design-system/src/components";
import {
  LikeOutlined,
  ReadOutlined,
  ArrowRightOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { User } from "../../../../types";
import {
  collectionApi,
  eventApi,
  storyApi,
} from "../../../../api/weserve/api-helper";
import { useMixpanel, useGA, useSegment } from "../../../../hooks";
import { useRouter } from "next/router";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "../../../../utils/inspire-analytics-helper";

export interface CardActionProps {
  id?: number;
  cardType?: string;
  user?: User;
  likeEnabled?: boolean;
  storiesEnabled?: boolean;
  shareEnabled?: boolean;
  userHasLiked?: boolean;
  likes?: number;
  numOfStories?: number;
  ctaTitle?: string;
  ctaLink?: string;
}

const CardActions = ({
  id,
  cardType,
  user,
  likeEnabled = true,
  storiesEnabled,
  shareEnabled,
  userHasLiked = false,
  likes = 0,
  numOfStories,
  ctaTitle,
  ctaLink,
}: CardActionProps) => {
  // Set state for "Like" icon, which is always enabled by default
  const [totalLikes, setTotalLikes] = useState(likes);
  const [userLiked, setUserLiked] = useState(userHasLiked);

  const router = useRouter();

  // Set up analytics
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();

  // Store total number of stories associated with event.
  const totalStories = numOfStories;

  // People aggregates fired only when a user is *adding* a like.
  //
  // BEHAVIOR CHANGE vs. legacy: `app/js/directives/luvv-it.js` called
  // `sendMxpnl()` from both the like and the unlike branch (lines 58-70),
  // so `Last Like TS` got re-stamped and `zzBMVP1: Total Likes` got
  // incremented on every click — including unlikes. We deliberately gate
  // on `!userLiked` here because the legacy double-fire produced a counter
  // that over-counts toggles rather than unique likes, which was probably
  // never the intent. If strict legacy parity is needed for a downstream
  // cohort, drop the `if (userLiked) return;` guard.
  //
  // Sources differ by property:
  //   - "Last Like TS" + "zzBMVP1: Total Likes" come from legacy
  //     `app/js/directives/luvv-it.js`.
  //   - "# of Likes" comes from legacy `platform/components/iconLuvvit/
  //     index.js` (different legacy product surface; not present in
  //     `app/`). Kept here so cohorts built on the platform-side property
  //     continue to see new likes.
  const trackLikePeopleAggregates = () => {
    if (userLiked) return;
    const nowTs = +new Date();
    InspireAnalytics.setPeopleProperties({
      mixpanel,
      properties: { "Last Like TS": nowTs },
    });
    InspireAnalytics.incrementPeopleProperty({
      mixpanel,
      property: "# of Likes",
    });
    InspireAnalytics.incrementPeopleProperty({
      mixpanel,
      property: "zzBMVP1: Total Likes",
    });
  };

  const toggleLikeApi = () => {
    if (!id) {
      console.info("ID not defined.");
      return;
    }
    const pageType = InspireAnalyticsHelper.getPageType(router.route);
    const likeEvent = InspireAnalyticEvents.LIKE;

    if (cardType === "event" || cardType === "fundraiser") {
      eventApi.toggleEventLike(id).catch((err) => console.error(err));
      googleAnalytics.event({
        category: "Like",
        action: "Clicked",
        label: `Type: Event, Location: ${window.location.href}`,
      });
      InspireAnalytics.track({
        eventName: likeEvent,
        properties: {
          "Card Type": cardType === "event" ? "Event" : "Fundraiser",
          "Page Type": pageType,
        },
        mixpanel,
        segment,
      });
      return;
    }
    if (cardType === "story") {
      storyApi.toggleStoryLike(id).catch((err) => console.error(err));
      googleAnalytics.event({
        category: "Like",
        action: "Selected",
        label: "Type:volunteer - Location: ", // How likes were being recorded in legacy app.
        value: 1,
      });
      InspireAnalytics.track({
        eventName: likeEvent,
        properties: {
          "Card Type": "Story",
          "Page Type": pageType,
        },
        mixpanel,
        segment,
      });
      return;
    }
    if (cardType === "collection") {
      collectionApi.toggleCollectionLike(id).catch((err) => console.error(err));
      googleAnalytics.event({
        category: "Like",
        action: "Clicked",
        label: `Type: Event, Location: ${window.location.href}`,
      });
      InspireAnalytics.track({
        eventName: likeEvent,
        properties: {
          "Card Type": "Collection",
          "Page Type": pageType,
        },
        mixpanel,
        segment,
      });
      return;
    }
    console.error(
      "ERROR: Card Type Not Specified. Please pass cardType prop into <CardActions> component connected to this card."
    );
    console.error("OPTIONS: event, fundraiser, story, collection");
    return;
  };

  const handleLike = () => {
    // If the user has not liked previously, increase the count of likes and flag as liked by that user.
    // But if the user has already liked, decrease the count and unflag the story as liked by the user.
    // Api call happens after frontend update for instantaneous UX.
    if (userLiked === false) {
      trackLikePeopleAggregates();
      setTotalLikes(totalLikes ? totalLikes + 1 : 1);
      setUserLiked(true);
      toggleLikeApi();
    } else {
      setTotalLikes(totalLikes ? totalLikes - 1 : 0);
      setUserLiked(false);
      toggleLikeApi();
    }
  };

  const handleShare = () => {
    // Currently does nothing. This is placeholder. TODO: Delete if not going to be used.
  };

  const handleCtaClick = () => {
    window.location.href = ctaLink || "/404";
  };

  return (
    <Group
      xs="horizontal"
      spacing={4}
      className="items-center justify-between ph-5 pv-4"
    >
      {/* ACTION BAR ----------------------------------------- */}
      <Group spacing={4} xs="horizontal" className="items-center">
        {/* LIKE ICON*/}
        {/* Usage - Displays total likes and highlights if a user likes */}
        {likeEnabled &&
          // Enable liking if User is logged in.
          (user ? (
            <Tooltip
              placement="bottom"
              title={userLiked ? "You like this" : "Like"}
              mouseEnterDelay={0.5}
              mouseLeaveDelay={0}
            >
              <a
                onClick={handleLike}
                className={`${
                  // Highlight icon if user already liked
                  userLiked ? "primary" : "color-inherit"
                }`}
              >
                {/* Icon */}
                <div className="hlight-primary flex items-center">
                  <LikeOutlined />
                  <p className="mv-auto ml-1 mr-0 usn">
                    {/* Like Count - Don't display a number if total likes = 0 */}
                    {totalLikes !== 0 ? totalLikes : ""}
                  </p>
                </div>
              </a>
            </Tooltip>
          ) : (
            // Disable liking if no use is logged in
            <Tooltip
              placement="bottom"
              title={"Sign in to like"}
              mouseEnterDelay={0.5}
              mouseLeaveDelay={0}
            >
              <div
                className="gray-6 notAllowed"
                onClick={() => {
                  message.info("Please sign in to like.");
                }}
              >
                <div className=" flex items-center">
                  <LikeOutlined />
                  <p className="mv-auto ml-1 mr-0 usn">
                    {/* Like Count - Don't display a number if total likes = 0 */}
                    {totalLikes !== 0 ? totalLikes : ""}
                  </p>
                </div>
              </div>
            </Tooltip>
          ))}

        {/* STORIES ICON */}
        {/* Usage - Displays total stories and links user to story page. */}
        {storiesEnabled && (
          <Tooltip
            placement="bottom"
            title="View stories"
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0}
          >
            <div>
              {ctaLink && (
                <a className="color-inherit" onClick={handleCtaClick}>
                  <div className="hlight-primary flex items-center">
                    <ReadOutlined />
                    {/* Story count */}
                    <p className="mv-auto ml-1 mr-0 usn">{totalStories}</p>
                  </div>
                </a>
              )}
            </div>
          </Tooltip>
        )}

        {/* Share Icon */}
        {/* Usage - Currently does nothing. Could be developed to display share options and display total shares. */}
        {shareEnabled && (
          <Tooltip
            placement="bottom"
            title="Share"
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0}
          >
            {/* Icon */}
            <a className={`color-inherit`} onClick={handleShare}>
              <div className="hlight-primary flex items-center">
                <ShareAltOutlined />
              </div>
            </a>
          </Tooltip>
        )}
      </Group>

      {/* CTA LINK ----------------------------------------- */}
      <Group>
        {ctaLink && (
          <div className="flex items-center justify-center width-100">
            <a onClick={handleCtaClick}>
              <div
                className={`flex items-center primary fwb f-3 borderUnderline`}
                style={{ borderBottom: "solid transparent 1px" }}
              >
                <p className="mv-auto mr-2 tc ttu">{ctaTitle}</p>
                <ArrowRightOutlined />
              </div>
            </a>
          </div>
        )}
      </Group>
    </Group>
  );
};

export default CardActions;
